import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import styled from 'styled-components';
import { modalFadeIn, modalFadeOut, modalZoomIn, modalZoomOut } from '../../styles/animations';

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const StyledOverlay = styled(DialogPrimitive.Overlay)`
  position: fixed;
  inset: 0;
  z-index: ${props => props.theme.zIndex.modalBackdrop};
  background-color: rgba(0, 0, 0, 0.8);
  
  &[data-state='open'] {
    animation: ${modalFadeIn} 200ms ease-out;
  }
  
  &[data-state='closed'] {
    animation: ${modalFadeOut} 200ms ease-out;
  }
`;

const StyledContent = styled(DialogPrimitive.Content)`
  position: fixed;
  left: 50%;
  top: 50%;
  z-index: ${props => props.theme.zIndex.modal};
  display: grid;
  width: 100%;
  max-width: 32rem;
  transform: translate(-50%, -50%);
  gap: ${props => props.theme.spacing.lg};
  border: 1px solid ${props => props.theme.colors.border.main};
  background-color: ${props => props.theme.colors.background.card};
  padding: ${props => props.theme.spacing['2xl']};
  box-shadow: ${props => props.theme.shadows.xl};
  transition: all 200ms;
  
  @media (min-width: ${props => props.theme.breakpoints.sm}) {
    border-radius: ${props => props.theme.borderRadius.lg};
  }
  
  &[data-state='open'] {
    animation: ${modalFadeIn} 200ms ease-out, ${modalZoomIn} 200ms ease-out;
  }
  
  &[data-state='closed'] {
    animation: ${modalFadeOut} 200ms ease-out, ${modalZoomOut} 200ms ease-out;
  }
  
  &:focus {
    outline: none;
  }
`;

const StyledCloseButton = styled(DialogPrimitive.Close)`
  position: absolute;
  right: ${props => props.theme.spacing.lg};
  top: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.sm};
  opacity: 0.7;
  transition: opacity ${props => props.theme.transitions.default};
  padding: ${props => props.theme.spacing.sm};
  
  &:hover {
    opacity: 1;
  }
  
  &:focus {
    outline: 2px solid ${props => props.theme.colors.primary.main};
    outline-offset: 2px;
  }
  
  svg {
    width: 1rem;
    height: 1rem;
    color: ${props => props.theme.colors.text.primary};
  }
`;

const DialogOverlay = React.forwardRef((props, ref) => (
  <StyledOverlay ref={ref} {...props} />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef(({ children, ...props }, ref) => (
  <DialogPortal>
    <StyledOverlay />
    <StyledContent ref={ref} {...props}>
      {children}
      <StyledCloseButton>
        <X />
        <span className="sr-only">Close</span>
      </StyledCloseButton>
    </StyledContent>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  text-align: center;
  
  @media (min-width: ${props => props.theme.breakpoints.sm}) {
    text-align: left;
  }
`;

const DialogFooter = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: ${props => props.theme.spacing.md};
  
  @media (min-width: ${props => props.theme.breakpoints.sm}) {
    flex-direction: row;
    justify-content: flex-end;
    gap: ${props => props.theme.spacing.md};
  }
`;

const DialogTitle = styled(DialogPrimitive.Title)`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  line-height: 1;
  letter-spacing: -0.025em;
  color: ${props => props.theme.colors.text.primary};
`;

const DialogDescription = styled(DialogPrimitive.Description)`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.muted};
`;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
